import { InfiniteTable } from '@/components/_common';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import { useInsurers } from '@/core/domain/people/Insurers/Insurers.hooks';
import {
  Insurer,
  ListInsurersQuery,
} from '@/core/domain/people/Insurers/Insurers.types';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { applyMask } from '@/utils/mask';
import { cnpjMask } from '@/utils/mask/commonMasks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Divider, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useIntl } from 'react-intl';

interface Props {
  handleRemoveClick: (record: Insurer) => void;
  params?: ListInsurersQuery;
}

const InsurersList: React.FC<Props> = ({ params, handleRemoveClick }) => {
  const intl = useIntl();
  const { data, isFetching, fetchNextPage, hasNextPage } = useInsurers(params);

  const insurers = flatInfiniteData(data);

  const columns: ColumnsType<Insurer> = [
    {
      dataIndex: 'cnpj',
      title: intl.formatMessage({ id: 'insurers.list.header.cnpj' }),
      ellipsis: true,
      width: 230,
      render: (value) => (value && applyMask(value, cnpjMask)) || '-',
    },
    {
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'insurers.list.header.name' }),
      ellipsis: true,
      width: 200,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'email',
      title: intl.formatMessage({ id: 'insurers.list.header.email' }),
      ellipsis: true,
      width: 250,
      render: (value) => value || '-',
    },
    {
      title: intl.formatMessage({ id: 'insurers.list.header.actions' }),
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (row) => (
        <Space>
          <EditIcon
            redirect={`/app/registry/insurers/${row.id}/general`}
            disabled={!hasPermissions([Authorities.REGISTER_INSURER_VIEW])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={!hasPermissions([Authorities.REGISTER_INSURER_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <InfiniteTable
      columns={columns}
      dataSource={insurers}
      loading={isFetching}
      rowKey={(row) => row.id}
      pagination={false}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
    />
  );
};

export default InsurersList;
